/**
 * WorkbenchShell — Shared Code Resolution Workbench layout shell
 *
 * Phase 1: extracted from ResolveIssuesView (Pre-Flight).
 * All Code Resolution Workbench modes share this shell:
 *   preflight, code_analysis, code_refactor, service_generation, java_package
 *
 * Layout zones:
 *   rv-root
 *   ├── rv-header        back btn | headerIcon | title | contextTag | headerStatus | headerActions
 *   ├── statusBar        persistent build/state bar (rendered as-is from prop)
 *   ├── rv-body          Allotment: left rail (collapsible) | center content
 *   │   └── children     overlays / modals injected by mode component
 *   ├── rv-toast-container
 *   └── rv-output-drawer (fixed bottom log panel, toggled by outputDrawerOpen)
 *
 * Shell owns:   layout chrome, Allotment split, sidebar collapse, toasts, output drawer
 * Mode owns:    data loading, actions, leftRail content, centerContent, headerActions,
 *               statusBar content, outputDrawerContent, modal/overlay children
 *
 * CSS: WorkbenchShell.css (structural chrome — rv- prefix)
 * Mode CSS: each mode imports its own file for mode-specific classes
 *
 * Future mode usage:
 *   import WorkbenchShell from '../workbench/WorkbenchShell'
 *   <WorkbenchShell title="..." leftRail={<ModeLeftRail />} centerContent={<ModeEditor />} ...>
 *     {modals}
 *   </WorkbenchShell>
 */

import { Allotment } from 'allotment'
import { ArrowLeft, PanelLeft, CheckCircle, XCircle, Activity } from 'lucide-react'
import 'allotment/dist/style.css'
import './WorkbenchShell.css'

export default function WorkbenchShell({
  // ── Header ──────────────────────────────────────────────────────────────
  onBack,
  backLabel = 'Back',
  headerIcon,      // ReactNode — icon between back btn and title (e.g. Zap, ShieldCheck)
  title,           // string — screen title
  contextTag,      // string — muted context pill (e.g. "Compile Check")
  headerStatus,    // ReactNode — status chips (blockers, pass badge, etc.)
  headerActions,   // ReactNode — right-side header buttons (Save, Build, Resume, etc.)

  // ── Status bar ───────────────────────────────────────────────────────────
  // Full div including rv-status-bar class and phase modifier — rendered directly
  statusBar,

  // ── Left rail ────────────────────────────────────────────────────────────
  // hideSidebar: skip the Allotment entirely — centerContent fills full body width.
  // Use when centerContent owns its own internal navigation layout (e.g. CodeEditorView
  // with its own SectionNavigator + COBOL panel). Prevents the double-rail state.
  hideSidebar = false,
  sidebarCollapsed = false,
  onSidebarCollapse,               // () => void — called when collapse btn clicked
  onSidebarExpand,                 // () => void — called when expand btn clicked
  sidebarLabel = '',               // string — shown in collapsed icon-rail state
  leftRail,                        // ReactNode — full sidebar content when expanded
  sidebarMinSize = 200,
  sidebarMaxSize = 440,
  sidebarPreferredSize = 280,

  // ── Center ───────────────────────────────────────────────────────────────
  centerContent,                   // ReactNode
  centerMinSize = 400,

  // ── Output drawer (fixed bottom log panel) ───────────────────────────────
  outputDrawerOpen = false,
  outputDrawerContent,             // ReactNode — header + body inside the drawer

  // ── Toasts ───────────────────────────────────────────────────────────────
  toasts = [],                     // [{ id, type, message }]
  onDismissToast,                  // (id) => void

  // ── Overlays / modals (rendered inside rv-body) ──────────────────────────
  children,
}) {
  return (
    <div className="rv-root">

      {/* ── Header ── */}
      <div className="rv-header">
        <button className="rv-back-btn" onClick={onBack}>
          <ArrowLeft size={14} />
          {backLabel}
        </button>

        {headerIcon}

        <span className="rv-header-title">{title}</span>

        {contextTag && (
          <span className="rv-header-context">{contextTag}</span>
        )}

        {/* Spacer — pushes headerStatus + headerActions to the right edge.
            flex:1 lives here, not on rv-header-context, so the context pill stays compact. */}
        <span className="rv-header-spacer" />

        {headerStatus}

        {headerActions}
      </div>

      {/* ── Status bar ── */}
      {statusBar}

      {/* ── Body: left rail | center content ── */}
      <div className="rv-body">

        {hideSidebar ? (
          // Mode owns its own internal navigation layout (e.g. CodeEditorView has
          // SectionNavigator + COBOL panel as its own Allotment panes).
          // Skip the shell Allotment entirely — centerContent fills the full body.
          <div className="rv-body-fill">
            {centerContent}
          </div>
        ) : (
          // Standard split: collapsible left rail + center content
          // key forces Allotment remount so defaultSizes take effect on collapse toggle
          <Allotment
            key={sidebarCollapsed ? 'c' : 'e'}
            defaultSizes={sidebarCollapsed ? [48, 900] : [sidebarPreferredSize, 900]}
          >

            {/* Left pane — collapsible issues/explorer rail */}
            <Allotment.Pane
              minSize={sidebarCollapsed ? 48 : sidebarMinSize}
              maxSize={sidebarCollapsed ? 48 : sidebarMaxSize}
              preferredSize={sidebarCollapsed ? 48 : sidebarPreferredSize}
            >
              {sidebarCollapsed ? (
                <div className="rv-sidebar-collapsed">
                  <button
                    className="rv-collapse-toggle"
                    onClick={onSidebarExpand}
                    title="Expand Panel"
                  >
                    <PanelLeft size={18} />
                  </button>
                  <span className="rv-collapsed-label">{sidebarLabel}</span>
                </div>
              ) : (
                leftRail
              )}
            </Allotment.Pane>

            {/* Center pane — editor / main content */}
            <Allotment.Pane minSize={centerMinSize}>
              {centerContent}
            </Allotment.Pane>

          </Allotment>
        )}

        {/* Overlays / modals injected by mode component */}
        {children}
      </div>

      {/* ── Toast notifications ── */}
      <div className="rv-toast-container">
        {toasts.map(t => (
          <div key={t.id} className={`rv-toast rv-toast--${t.type}`}>
            {t.type === 'success' && <CheckCircle size={13} />}
            {t.type === 'error'   && <XCircle size={13} />}
            {t.type === 'info'    && <Activity size={13} />}
            <span>{t.message}</span>
            <button
              className="rv-toast-close"
              onClick={() => onDismissToast?.(t.id)}
            >×</button>
          </div>
        ))}
      </div>

      {/* ── Output drawer (fixed bottom log panel) ── */}
      {outputDrawerOpen && outputDrawerContent && (
        <div className="rv-output-drawer">
          {outputDrawerContent}
        </div>
      )}

    </div>
  )
}
